<template>
  <v-dialog
    :value="open"
    max-width="900"
    persistent
    content-class="offer-modal-dialog"
    @input="onDialogInput"
  >
    <v-card :loading="isLoading" class="offer-modal-card">
      <div class="offer-modal-layout">
        <div class="offer-modal-header">
          <div class="offer-modal-title">
            {{ $t("OFFER.PREVIEW_TITLE") }}
            <span v-if="offer"> #{{ offer.id }}</span>
          </div>
        </div>

        <div class="offer-modal-scroll">
          <template v-if="offer">
            <div class="offer-section">
              <div class="offer-section-title">
                {{ $t("OFFER.SECTION_GENERAL") }}
              </div>
              <v-row dense>
                <v-col cols="12" sm="6">
                  <div class="offer-field">
                    <span class="offer-field-label">{{ $t("GENERAL.LABELS.CONTACT") }}</span>
                    <span class="offer-field-value">{{ offer.contact_name || "-" }}</span>
                  </div>
                </v-col>
                <v-col cols="12" sm="6">
                  <div class="offer-field">
                    <span class="offer-field-label">{{ $t("GENERAL.LABELS.EMAIL") }}</span>
                    <span class="offer-field-value">{{ offer.contact_email || "-" }}</span>
                  </div>
                </v-col>
                <v-col cols="12" sm="6">
                  <div class="offer-field">
                    <span class="offer-field-label">{{ $t("GENERAL.LABELS.COMPANY") }}</span>
                    <span class="offer-field-value">{{ offer.company_name || "-" }}</span>
                  </div>
                </v-col>
                <v-col cols="12" sm="6">
                  <div class="offer-field">
                    <span class="offer-field-label">{{ $t("GENERAL.LABELS.STATUS") }}</span>
                    <span class="offer-field-value">
                      {{ offer.is_active ? $t("GENERAL.LABELS.ACTIVE") : $t("GENERAL.LABELS.INACTIVE") }}
                    </span>
                  </div>
                </v-col>
                <v-col cols="12" sm="6">
                  <div class="offer-field">
                    <span class="offer-field-label">{{ $t("LIST_OFFERS.DATE") }}</span>
                    <span class="offer-field-value">{{ offer.date || "-" }}</span>
                  </div>
                </v-col>
                <v-col cols="12" sm="6">
                  <div class="offer-field">
                    <span class="offer-field-label">{{ $t("LIST_OFFERS.EXPIRE_DATE") }}</span>
                    <span class="offer-field-value">{{ offer.expire_date || "-" }}</span>
                  </div>
                </v-col>
                <v-col cols="12">
                  <div class="offer-field">
                    <span class="offer-field-label">{{ $t("COMPANIES.NOTES") }}</span>
                    <span class="offer-field-value">{{ offer.notes || "-" }}</span>
                  </div>
                </v-col>
                <v-col cols="12">
                  <div class="offer-field">
                    <span class="offer-field-label">{{ $t("LIST_OFFERS.RAW") }}</span>
                    <span class="offer-field-value offer-field-raw">{{ offer.raw || "-" }}</span>
                  </div>
                </v-col>
              </v-row>
            </div>

            <div class="offer-section" v-if="tagRows.length">
              <div class="offer-section-title">{{ $t("OFFER.SECTION_TAGS") }}</div>
              <v-simple-table dense>
                <tbody>
                  <tr v-for="(row, index) in tagRows" :key="`tag-${index}`">
                    <td class="offer-cell-label">{{ row.label }}</td>
                    <td>{{ row.value != null && row.value !== "" ? row.value : "-" }}</td>
                  </tr>
                </tbody>
              </v-simple-table>
            </div>

            <div class="offer-section" v-if="mappingRows.length">
              <div class="offer-section-title">{{ $t("OFFER.SECTION_MAPPINGS") }}</div>
              <v-simple-table dense>
                <tbody>
                  <tr v-for="(row, index) in mappingRows" :key="`mapping-${index}`">
                    <td class="offer-cell-label">{{ row.label }}</td>
                    <td>{{ row.value != null && row.value !== "" ? row.value : "-" }}</td>
                  </tr>
                </tbody>
              </v-simple-table>
            </div>

            <div class="offer-section" v-if="normalizedRows.length">
              <div class="offer-section-title">{{ $t("OFFER.SECTION_NORMALIZE") }}</div>
              <v-simple-table dense>
                <tbody>
                  <tr v-for="(row, index) in normalizedRows" :key="`nd-${index}`">
                    <td class="offer-cell-label">{{ row.label }}</td>
                    <td>{{ row.value }}</td>
                  </tr>
                </tbody>
              </v-simple-table>
            </div>
          </template>
        </div>

        <div class="offer-modal-footer">
          <v-spacer></v-spacer>
          <v-btn text @click="$emit('close')">{{ $t("GENERAL.LABELS.CLOSE_BUTTON") }}</v-btn>
          <v-btn
            v-if="editRoute"
            color="primary"
            :to="editRoute"
          >
            {{ $t("GENERAL.LABELS.EDIT_BUTTON") }}
          </v-btn>
        </div>
      </div>
    </v-card>
  </v-dialog>
</template>

<script>
import { GET_VENDOR_OFFER } from "@/store/vendorOffer.module";
import { SHOW_SNACK } from "@/store/snack.module";

export default {
  name: "OfferModal",
  props: {
    open: {
      type: Boolean,
      required: true,
    },
    offerId: {
      type: [Number, String],
      default: null,
    },
  },
  data() {
    return {
      offer: null,
      isLoading: false,
    };
  },
  computed: {
    tagRows() {
      return (this.offer && this.offer.tags) || [];
    },
    mappingRows() {
      return (this.offer && this.offer.mapping_values) || [];
    },
    normalizedRows() {
      if (!this.offer) {
        return [];
      }

      return [
        { key: "normalized_price", label: "Normalized Price", value: this.offer.normalized_price },
        { key: "normalized_quantity", label: "Normalized Quantity", value: this.offer.normalized_quantity },
        { key: "is_offer_active", label: "Offer Active", value: this.offer.is_offer_active },
        { key: "date_week", label: "Date Week", value: this.offer.date_week },
      ]
        .filter((row) => row.value !== null && row.value !== "" && row.value !== undefined)
        .map((row) => ({
          ...row,
          value: this.stringifyValue(row.value),
        }));
    },
    editRoute() {
      if (!this.offer || !this.offer.offer_set_id || !this.hasPermissions("offers-edit")) {
        return null;
      }

      return {
        name: "edit-offer",
        params: { id: parseInt(this.offer.offer_set_id, 10) },
      };
    },
  },
  watch: {
    open: {
      immediate: true,
      handler(value) {
        if (value) {
          this.loadOffer();
        } else {
          this.offer = null;
        }
      },
    },
  },
  methods: {
    stringifyValue(value) {
      if (value === null || value === undefined) {
        return "-";
      }

      if (typeof value === "object") {
        return value.value !== undefined ? value.value : JSON.stringify(value);
      }

      return value;
    },
    loadOffer() {
      if (!this.offerId) {
        return;
      }

      this.offer = null;
      this.isLoading = true;

      this.$store
        .dispatch(GET_VENDOR_OFFER, this.offerId)
        .then((offer) => {
          this.offer = offer;
        })
        .catch(() => {
          this.$store.dispatch(SHOW_SNACK, {
            message: this.$t("GENERAL.UNEXPECTED_ERROR"),
            type: "error",
          });
          this.$emit("close");
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
    onDialogInput(value) {
      if (!value) {
        this.$emit("close");
      }
    },
  },
};
</script>

<style scoped>
.offer-modal-card {
  height: 100%;
  overflow: hidden;
}

.offer-modal-layout {
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;
}

.offer-modal-header {
  flex: 0 0 auto;
  background: #fff;
}

.offer-modal-title {
  padding: 16px 24px 12px;
  font-size: 1.25rem;
  font-weight: 500;
}

.offer-modal-scroll {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 8px 24px 16px;
  -webkit-overflow-scrolling: touch;
}

.offer-section {
  margin-bottom: 20px;
}

.offer-section-title {
  font-size: 1rem;
  font-weight: 600;
  margin-bottom: 8px;
  color: #1976d2;
}

.offer-field {
  display: flex;
  flex-direction: column;
  margin-bottom: 8px;
}

.offer-field-label {
  font-size: 0.75rem;
  color: #7f8899;
}

.offer-field-value {
  font-size: 0.9rem;
}

.offer-field-raw {
  white-space: pre-wrap;
  word-break: break-word;
}

.offer-cell-label {
  font-weight: 500;
  width: 40%;
}

.offer-modal-footer {
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  padding: 12px 16px;
  border-top: 1px solid #e3e6ef;
  background: #fff;
}
</style>

<style>
.offer-modal-dialog {
  height: calc(100vh - 96px);
  max-height: calc(100vh - 96px);
  overflow: hidden;
}

.offer-modal-dialog > .v-card {
  height: 100%;
}
</style>
